import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { TagMapping } from '../models/tag-mapping.model';

const BASE_URL = '/marketplace-service/api/v1';

interface BadgeResponse {
  name: string;
  ids: string[];
}

export interface ProductActivationResponse {
  activationStatus: boolean;
  canBeEnabled: boolean;
  productId: string;
}

export interface ContractPriceEntry {
  productId: string;
  productName: string;
  price: number;
  uoM: string;
  durationId?: string;
}

export type ContractPricesResponse = ContractPriceEntry[];

@Injectable({ providedIn: 'root' })
export class MarketplaceServiceClient {
  private http = inject(HttpClient);

  checkHealth(): Observable<string> {
    return this.http.get(`${BASE_URL}/health`, { responseType: 'text' });
  }

  getContractPrices(contractId: string): Observable<ContractPricesResponse> {
    return this.http.get<ContractPricesResponse>(`${BASE_URL}/prices/contracts/${contractId}`);
  }

  getProductActivation(productId: string, billingAccountId: string): Observable<ProductActivationResponse> {
    return this.http.get<ProductActivationResponse>(`${BASE_URL}/product/${productId}/activation`, {
      headers: new HttpHeaders({ billingAccountId }),
    });
  }

  activateProduct(productId: string, billingAccountId: string): Observable<void> {
    return this.http.put<void>(`${BASE_URL}/product/${productId}/activation`, null, {
      headers: new HttpHeaders({ billingAccountId }),
    });
  }

  deactivateProduct(productId: string, billingAccountId: string): Observable<void> {
    return this.http.delete<void>(`${BASE_URL}/product/${productId}/activation`, {
      headers: new HttpHeaders({ billingAccountId }),
    });
  }

  getBadges(): Observable<TagMapping> {
    return this.http.get<BadgeResponse[]>(`${BASE_URL}/badges`).pipe(
      map(badges => {
        const mapping: TagMapping = {};
        for (const badge of badges) {
          for (const id of badge.ids) {
            mapping[id] = [...(mapping[id] ?? []), badge.name];
          }
        }
        // zz-proto is the non-SAP test product — permanently tagged coming-soon for local testing
        mapping['zz-proto'] = [...(mapping['zz-proto'] ?? []), 'coming-soon'];
        return mapping;
      })
    );
  }
}
